/**
 * frontend/src/pages/CopyrightPolicy.jsx — /legal/copyright (public).
 *
 * DMCA §512(c) notice-and-takedown policy. Creators upload images and video, so the safe harbour
 * needs (1) a designated agent registered with the U.S. Copyright Office and (2) the agent's
 * contact details on the site. The details come from build-time env vars so nothing here is a
 * placeholder pretending to be real:
 *   REACT_APP_DMCA_AGENT_NAME, REACT_APP_DMCA_AGENT_ADDRESS, REACT_APP_DMCA_AGENT_EMAIL,
 *   REACT_APP_DMCA_AGENT_PHONE (optional)
 */
import React from 'react';
import { Link } from 'react-router-dom';
import BrandMark from '../components/BrandMark';

const AGENT = {
  name:    process.env.REACT_APP_DMCA_AGENT_NAME || '',
  address: process.env.REACT_APP_DMCA_AGENT_ADDRESS || '',
  email:   process.env.REACT_APP_DMCA_AGENT_EMAIL || '',
  phone:   process.env.REACT_APP_DMCA_AGENT_PHONE || '',
};
const hasAgent = Boolean(AGENT.name && AGENT.address && AGENT.email);

const wrap = { maxWidth: 680, margin: '0 auto', padding: '32px 16px 64px', lineHeight: 1.6, color: 'inherit' };

export default function CopyrightPolicy() {
  return (
    <div style={wrap}>
      <Link to="/" aria-label="SoundDAO home"><BrandMark /></Link>
      <h1>Copyright &amp; DMCA policy</h1>
      <p>
        SoundDAO respects the rights of artists and copyright owners. Creators may upload images and
        video. If you believe something on SoundDAO infringes your copyright, send a notice to our
        designated agent. We remove or disable access to infringing material promptly and terminate
        the accounts of repeat infringers.
      </p>

      <h2>Designated agent</h2>
      {hasAgent ? (
        <address style={{ fontStyle: 'normal' }}>
          {AGENT.name}<br />
          {AGENT.address}<br />
          <a href={`mailto:${AGENT.email}`}>{AGENT.email}</a>
          {AGENT.phone && <><br />{AGENT.phone}</>}
        </address>
      ) : (
        <p>Our designated agent's contact details are being registered with the U.S. Copyright Office and will appear here.</p>
      )}

      <h2>What a notice must include</h2>
      <ol>
        <li>Your physical or electronic signature.</li>
        <li>Identification of the copyrighted work you say is infringed.</li>
        <li>Identification of the material on SoundDAO, with enough detail (such as the URL) for us to find it.</li>
        <li>Your name, address, telephone number and email address.</li>
        <li>A statement that you believe in good faith that the use is not authorised by the copyright owner, its agent or the law.</li>
        <li>A statement, under penalty of perjury, that the information in your notice is accurate and that you are the owner or authorised to act for the owner.</li>
      </ol>

      <h2>Counter-notice</h2>
      <p>
        If your material was removed and you believe that was a mistake or misidentification, you may
        send our agent a counter-notice with your signature, identification of the removed material
        and where it appeared, a statement under penalty of perjury that you believe it was removed
        by mistake or misidentification, and your name, address and telephone number with consent
        to the jurisdiction of the federal district court for your address (or, outside the U.S.,
        any district where SoundDAO may be found). Unless the complainant tells us within 10–14
        business days that they have filed suit, we may restore the material.
      </p>

      <h2>Repeat infringers</h2>
      <p>We terminate, in appropriate circumstances, the accounts of users who repeatedly infringe. See our <Link to="/legal/terms">Terms of Service</Link>.</p>

      <h2>Recordings from the Internet Archive</h2>
      <p>
        Live recordings played in SoundDAO are streamed from the Internet Archive and are not hosted by
        SoundDAO. To have a recording removed at its source, contact the Internet Archive.
      </p>
    </div>
  );
}
